import { loadState, loadHistory, loadTrainingDates, computeStreak, longestStreak } from "@/storage/routine";
import { loadWeeks, snapshotCycle } from "@/storage/weeks";
import { loadMeasurements, getAllMetrics } from "@/storage/measurements";
import { loadWeeklyGoal } from "@/storage/weeklyGoal";
import { loadDiet } from "@/storage/diet";

// Retrato completo de lo que ha entrenado un usuario, en una estructura plana
// que no depende de cómo esté guardado nada por debajo.
//
// El informe HTML y el PDF leen los dos de aquí: son dos maquetaciones del
// mismo modelo, así que una cifra nueva aparece en ambos sin tocarlos.
//
// Las imágenes se quedan fuera a propósito. Las de la biblioteca son GIFs de un
// CDN, que en un archivo guardado en el móvil solo cargarían con conexión, y
// las personalizadas son data URIs que ya rozan el límite de sincronización
// (ver lib/sync.js): meterlas convertiría un informe de 200 KB en uno de varios
// megas, y en papel un GIF animado no aporta nada.

const EMPTY_TREND = { first: null, last: null, best: null, delta: null, percent: null, sessions: 0 };

// Primer peso, último y récord de un ejercicio, con la mejora entre extremos.
// El historial guarda una entrada por día y ejercicio, así que su longitud son
// las sesiones en las que se tocó ese peso, no las series hechas.
function trend(entries) {
  if (!entries || entries.length === 0) return { ...EMPTY_TREND };

  const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date));
  const first = sorted[0].weight;
  const last = sorted[sorted.length - 1].weight;
  const best = Math.max(...sorted.map((e) => e.weight));

  return {
    first,
    last,
    best,
    firstDate: sorted[0].date,
    lastDate: sorted[sorted.length - 1].date,
    delta: Number((last - first).toFixed(2)),
    percent: first > 0 ? Math.round(((last - first) / first) * 100) : null,
    sessions: sorted.length,
  };
}

function cycleTotals(days) {
  let sets = 0;
  let volume = 0;
  days.forEach((day) => {
    day.exercises.forEach((ex) => {
      sets += ex.sets.length;
      ex.sets.forEach((s) => {
        volume += (s.weight || 0) * (s.reps || 0);
      });
    });
  });
  return { sets, volume: Math.round(volume) };
}

export function buildExportModel({ email } = {}) {
  const state = loadState();
  const weeks = loadWeeks();
  const history = loadHistory();
  const trainingDates = loadTrainingDates();
  const measurements = loadMeasurements();
  const metrics = getAllMetrics();

  // El ciclo en curso no está archivado, pero lo entrenado en él ya cuenta:
  // dejarlo fuera del informe restaría las series de esta misma semana.
  const current = snapshotCycle(state.routine, state.progress, state.alternatives || {});
  const currentTotals = cycleTotals(current.days);

  const archivedSets = weeks.reduce((a, w) => a + (w.totalSets || 0), 0);
  const archivedVolume = weeks.reduce((a, w) => a + (w.totalVolume || 0), 0);

  // Series por grupo muscular a lo largo de todo el historial, para ver de un
  // vistazo qué se ha trabajado de más y qué se ha ido dejando.
  const volumeByGroup = {};
  [...weeks.map((w) => w.volumeByGroup || {}), current.volumeByGroup].forEach((map) => {
    Object.entries(map).forEach(([group, count]) => {
      volumeByGroup[group] = (volumeByGroup[group] || 0) + count;
    });
  });

  const exercises = Object.entries(history)
    .map(([name, entries]) => ({ name, ...trend(entries), entries: [...entries].sort((a, b) => a.date.localeCompare(b.date)) }))
    .sort((a, b) => b.sessions - a.sessions || a.name.localeCompare(b.name, "es"));

  const measured = metrics
    .map((metric) => {
      const entries = measurements[metric.id] || [];
      if (entries.length === 0) return null;
      const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date));
      const first = sorted[0].value;
      const last = sorted[sorted.length - 1].value;
      return {
        id: metric.id,
        name: metric.name,
        unit: metric.unit,
        custom: !!metric.custom,
        entries: sorted,
        first,
        last,
        delta: Number((last - first).toFixed(2)),
        firstDate: sorted[0].date,
        lastDate: sorted[sorted.length - 1].date,
      };
    })
    .filter(Boolean);

  const sortedDates = [...trainingDates].sort();

  // Cómo fue el último ciclo cerrado frente al anterior. Es la única
  // comparación honesta que se puede hacer sin inventar una tendencia: el ciclo
  // en curso está a medias y siempre saldría peor.
  const closed = [...weeks].sort((a, b) => (b.week || 0) - (a.week || 0));
  const change = (recent, previous) =>
    previous > 0 ? Math.round(((recent - previous) / previous) * 100) : null;
  const lastCycle =
    closed.length >= 2
      ? {
          week: closed[0].week,
          sets: change(closed[0].totalSets || 0, closed[1].totalSets || 0),
          volume: change(closed[0].totalVolume || 0, closed[1].totalVolume || 0),
        }
      : null;

  return {
    meta: {
      app: "TRYAD",
      schema: 1,
      generatedAt: new Date().toISOString(),
      userName: state.userName || "",
      email: email || "",
    },
    summary: {
      cycle: state.week || 1,
      cycleStartedAt: state.weekStartedAt || null,
      cyclesCompleted: weeks.length,
      totalSets: archivedSets + currentTotals.sets,
      totalVolume: archivedVolume + currentTotals.volume,
      trainingDays: sortedDates.length,
      firstTrainingDate: sortedDates[0] || null,
      lastTrainingDate: sortedDates[sortedDates.length - 1] || null,
      currentStreak: computeStreak(trainingDates),
      bestStreak: longestStreak(trainingDates),
      weeklyGoal: loadWeeklyGoal(),
      exercisesTracked: exercises.length,
      lastCycle,
    },
    routine: {
      week: state.week || 1,
      startedAt: state.weekStartedAt || null,
      days: state.routine.map((day) => ({
        name: day.name,
        short: day.short,
        exercises: day.exercises.map((ex) => ({
          name: ex.name,
          muscleGroup: ex.muscleGroup || "",
          sets: ex.sets,
          reps: ex.reps || "",
        })),
      })),
    },
    currentCycle: { ...current, ...currentTotals },
    weeks: [...weeks].sort((a, b) => (b.week || 0) - (a.week || 0)),
    volumeByGroup,
    exercises,
    measurements: measured,
    trainingDates: sortedDates,
    // Va siempre, aunque no se haya tocado: quien nunca abrió la pantalla sigue
    // la dieta de fábrica, y es la suya igualmente.
    diet: loadDiet(),
  };
}

// tryad-alberto-2026-08-06.pdf
//
// NFD separa la tilde de su letra y el filtro ASCII se lleva la tilde suelta,
// de modo que "Jose Ramón" da "jose-ramon". Sin ese paso los acentos caerían en
// el reemplazo siguiente y saldría "jose-ram-n".
export function exportFileName(model, extension) {
  const slug = (model.meta.userName || "atleta")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[^\x00-\x7F]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "atleta";
  return `tryad-${slug}-${model.meta.generatedAt.split("T")[0]}.${extension}`;
}

export function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Safari lee el blob de forma asíncrona tras el click: revocarlo en el mismo
  // tick deja el archivo descargado vacío.
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
